import { type ModalComponent, type ModalRegistration } from './modal-registry.types';

/**
 * type → component, filled by the `app.config.tsx` bridge at import time.
 *
 * This is what lets a module open another module's dialog without importing it
 * (W2): the caller dispatches a string, and only the bridge knows which
 * component that string means. `shared/` therefore never reaches into
 * `modules/`.
 */
const components = new Map<string, ModalComponent>();

export const modalRegistry = {
  /**
   * Claims every type in the registration.
   *
   * Idempotent for the SAME component, because React 19 StrictMode invokes
   * effects twice and `initializeAppConfig()` would otherwise throw on the
   * second pass. A DIFFERENT component for a claimed type is a genuine
   * collision — two modules both naming their dialog `confirmDelete` — and
   * throws, because the alternative is that whichever registered last silently
   * wins and nothing in the codebase points at the loser.
   */
  register(registration: ModalRegistration): void {
    for (const type of registration.modalTypes) {
      const existing = components.get(type);

      if (existing !== undefined && existing !== registration.component) {
        throw new Error(
          `Modal type "${type}" is already registered to a different component. Modal types are global — rename one of them.`,
        );
      }

      components.set(type, registration.component);
    }
  },

  resolve(type: string): ModalComponent | undefined {
    return components.get(type);
  },

  /** Test seam. Never called by the app. */
  clear(): void {
    components.clear();
  },
};
